import { Component, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';

const PAGE_SIZE = 20;

/**
 * Read-only popup that itemises a patient's outstanding balance: one row per unpaid
 * statement/encounter, each expandable to the services inside it. Opened from the
 * outstanding-balance alert. Collecting happens on the billing/deposit screen, not here.
 *
 * Both lists are paged server-side (PagedResults<T> -> { Results, TotalRecords }); the
 * statements list and each statement's services load more on demand so a patient with a
 * long history never pulls everything at once. The footer total is the authoritative
 * balance (same number the alert shows), independent of how many pages are loaded.
 */
@Component({
    selector: 'outstanding-balance-breakdown',
    templateUrl: './outstanding-balance-breakdown.component.html',
})
export class OutstandingBalanceBreakdownComponent {
    @ViewChild('modal') modal: ModalDirective;

    loading = false;
    loadingMore = false;
    patientId: number;
    statements: any[] = [];
    total = 0;              // authoritative total owed (from the balance SP)
    totalStatements = 0;    // TotalRecords, drives the statements pager
    private page = 0;

    constructor(private http: HttpClient, private router: Router) { }

    open(patientId: number): void {
        this.patientId = patientId;
        this.statements = [];
        this.total = 0;
        this.totalStatements = 0;
        this.page = 0;
        this.modal.show();

        // Authoritative total owed - matches the alert, not affected by paging.
        this.http.get<number>(`${environment.queryModel}PatientStatmentSearch/PatientOutstandingBalance?patientId=${patientId}`)
            .subscribe(b => this.total = b || 0, () => { });

        this.loadStatements(true);
    }

    private loadStatements(first: boolean): void {
        if (first) { this.loading = true; } else { this.loadingMore = true; }

        this.http.get<any>(`${environment.queryModel}PatientStatmentSearch/PatientOutstandingStatements?patientId=${this.patientId}&page=${this.page}&rows=${PAGE_SIZE}`)
            .subscribe(res => {
                const rows = (res?.Results || []).map(r => ({
                    ...r, expanded: false, services: [], servicesTotal: 0,
                    servicesPage: 0, servicesLoaded: false, loadingServices: false,
                }));
                this.statements = this.statements.concat(rows);
                this.totalStatements = res?.TotalRecords ?? this.statements.length;
                this.loading = false;
                this.loadingMore = false;
            }, () => { this.loading = false; this.loadingMore = false; });
    }

    loadMoreStatements(): void {
        this.page++;
        this.loadStatements(false);
    }

    get canLoadMoreStatements(): boolean {
        return this.statements.length < this.totalStatements;
    }

    toggle(row: any): void {
        row.expanded = !row.expanded;
        if (row.expanded && !row.servicesLoaded) {
            this.loadServices(row);
        }
    }

    private loadServices(row: any): void {
        row.loadingServices = true;
        this.http.get<any>(`${environment.queryModel}PatientStatmentSearch/StatementUnpaidServices?statementId=${row.StatementId}&page=${row.servicesPage}&rows=${PAGE_SIZE}`)
            .subscribe(res => {
                row.services = row.services.concat(res?.Results || []);
                row.servicesTotal = res?.TotalRecords ?? row.services.length;
                row.servicesLoaded = true;
                row.loadingServices = false;
            }, () => { row.servicesLoaded = true; row.loadingServices = false; });
    }

    loadMoreServices(row: any): void {
        row.servicesPage++;
        this.loadServices(row);
    }

    canLoadMoreServices(row: any): boolean {
        return row.services.length < row.servicesTotal;
    }

    goCollect(): void {
        this.modal.hide();
        this.router.navigateByUrl(`/pages/patientAccounting/patientDeposit/${this.patientId}`);
    }
}
